import { create } from 'zustand';
import {
  UserProfile, UserSettings, InventoryItem, ShopItem,
  Transaction, Notification, Achievement,
} from '../shared/types';

interface UserStore {
  user: UserProfile | null;
  isLoggedIn: boolean;
  wallet: { balance: number; transactions: Transaction[] };
  notifications: Notification[];
  unreadCount: number;
  dailyRewardClaimed: boolean;
  weeklyRewardClaimed: boolean;

  login: (user: UserProfile) => void;
  logout: () => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  updateSettings: (updates: Partial<UserSettings>) => void;
  addItem: (item: InventoryItem) => void;
  removeItem: (itemId: string) => void;
  addCurrency: (amount: number, description: string) => void;
  spendCurrency: (amount: number, description: string) => boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  claimDailyReward: () => void;
  claimWeeklyReward: () => void;
  addAchievement: (achievement: Achievement) => void;
  equipCosmetic: (type: string, itemId: string) => void;
  getSettings: () => UserSettings;
}

const defaultSettings: UserSettings = {
  graphics: {
    quality: 'high',
    resolution: 1,
    fov: 76,
    vsync: false,
    fullscreen: true,
    shadows: true,
    antiAliasing: true,
    renderScale: 1,
  },
  audio: {
    master: 0.8,
    music: 0.6,
    sfx: 0.8,
    voice: 0.7,
    ambient: 0.5,
    spatialAudio: true,
  },
  controls: {
    mouseSensitivity: 2.6,
    adsSensitivity: 1.0,
    invertY: false,
    keybinds: {
      forward: 'KeyW',
      back: 'KeyS',
      left: 'KeyA',
      right: 'KeyD',
      jump: 'Space',
      crouch: 'ControlLeft',
      sprint: 'ShiftLeft',
      reload: 'KeyR',
      weapon1: 'Digit1',
      weapon2: 'Digit2',
      weapon3: 'Digit3',
    },
    crosshairColor: '#00e5ff',
    crosshairSize: 4,
    crosshairStyle: 'cross',
    controllerDeadzone: 0.15,
    controllerVibration: true,
  },
  gameplay: {
    autoReload: true,
    autoSwitchWeapon: false,
    showDamageNumbers: true,
    showFPS: true,
    showPing: true,
    language: 'en',
  },
  accessibility: {
    colorBlindMode: 'none',
    subtitles: false,
    screenShake: true,
    motionBlur: false,
    highContrast: false,
  },
};

const defaultUser: UserProfile = {
  id: 'local_user',
  username: 'Player',
  displayName: 'Player',
  avatar: '',
  level: 1,
  xp: 0,
  rank: 'bronze',
  title: 'Newcomer',
  banner: '',
  bio: '',
  createdAt: new Date().toISOString(),
  friends: [],
  followers: [],
  following: [],
  stats: {
    gamesPlayed: 0,
    wins: 0,
    kills: 0,
    deaths: 0,
    assists: 0,
    headshots: 0,
    accuracy: 0,
    playtime: 0,
    bestKillStreak: 0,
    bestScore: 0,
    xpEarned: 0,
    trEarned: 0,
  },
  inventory: [],
  cosmetics: {
    skin: 'default',
    title: 'Newcomer',
    banner: 'default',
    icon: 'default',
    emotes: [],
  },
  achievements: [],
  settings: defaultSettings,
};

export const useUserStore = create<UserStore>((set, get) => ({
  user: defaultUser,
  isLoggedIn: true,
  wallet: { balance: 500, transactions: [] },
  notifications: [],
  unreadCount: 0,
  dailyRewardClaimed: false,
  weeklyRewardClaimed: false,

  login: (user) => set({ user, isLoggedIn: true }),
  logout: () => set({ user: null, isLoggedIn: false }),

  updateProfile: (updates) => set((s) => ({
    user: s.user ? { ...s.user, ...updates } : null,
  })),

  updateSettings: (updates) => set((s) => ({
    user: s.user
      ? { ...s.user, settings: { ...s.user.settings, ...updates } }
      : null,
  })),

  addItem: (item) => set((s) => ({
    user: s.user
      ? { ...s.user, inventory: [...s.user.inventory, item] }
      : null,
  })),

  removeItem: (itemId) => set((s) => ({
    user: s.user
      ? { ...s.user, inventory: s.user.inventory.filter(i => i.id !== itemId) }
      : null,
  })),

  addCurrency: (amount, description) => set((s) => ({
    wallet: {
      balance: s.wallet.balance + amount,
      transactions: [
        {
          id: `txn_${Date.now()}`,
          type: amount > 0 ? 'reward' as const : 'purchase' as const,
          amount,
          balance: s.wallet.balance + amount,
          description,
          timestamp: new Date().toISOString(),
        },
        ...s.wallet.transactions,
      ].slice(0, 100),
    },
  })),

  spendCurrency: (amount, description) => {
    const state = get();
    if (state.wallet.balance < amount) return false;
    set({
      wallet: {
        balance: state.wallet.balance - amount,
        transactions: [
          {
            id: `txn_${Date.now()}`,
            type: 'purchase' as const,
            amount: -amount,
            balance: state.wallet.balance - amount,
            description,
            timestamp: new Date().toISOString(),
          },
          ...state.wallet.transactions,
        ].slice(0, 100),
      },
    });
    return true;
  },

  addNotification: (notification) => set((s) => {
    const notif: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      read: false,
      createdAt: new Date().toISOString(),
    };
    return {
      notifications: [notif, ...s.notifications].slice(0, 50),
      unreadCount: s.unreadCount + 1,
    };
  }),

  markNotificationRead: (id) => set((s) => ({
    notifications: s.notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ),
    unreadCount: Math.max(0, s.unreadCount - 1),
  })),

  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),

  claimDailyReward: () => set((s) => ({
    dailyRewardClaimed: true,
    wallet: {
      balance: s.wallet.balance + 50,
      transactions: [
        {
          id: `txn_${Date.now()}`,
          type: 'reward' as const,
          amount: 50,
          balance: s.wallet.balance + 50,
          description: 'Daily Login Reward',
          timestamp: new Date().toISOString(),
        },
        ...s.wallet.transactions,
      ],
    },
  })),

  claimWeeklyReward: () => set((s) => ({
    weeklyRewardClaimed: true,
    wallet: {
      balance: s.wallet.balance + 500,
      transactions: [
        {
          id: `txn_${Date.now()}`,
          type: 'reward' as const,
          amount: 500,
          balance: s.wallet.balance + 500,
          description: 'Weekly Reward',
          timestamp: new Date().toISOString(),
        },
        ...s.wallet.transactions,
      ],
    },
  })),

  addAchievement: (achievement) => set((s) => ({
    user: s.user
      ? { ...s.user, achievements: [...s.user.achievements, achievement] }
      : null,
  })),

  equipCosmetic: (type, itemId) => set((s) => ({
    user: s.user
      ? { ...s.user, cosmetics: { ...s.user.cosmetics, [type]: itemId } }
      : null,
  })),

  getSettings: () => get().user?.settings ?? defaultSettings,
}));
